/**
 * PageCurve.js
 *
 * Simulates the curvature of an open magazine page using a vertical-strip
 * mesh distortion.  Each page is divided into narrow vertical slices that are
 * displaced along a sine/cosine curve, compressed near the spine and flattened
 * at the outer edge.  A per-strip lighting overlay darkens the valley near the
 * spine and brightens the outer crest, producing a realistic 3-D page effect.
 *
 * All rendering targets the Canvas 2D API — the output is drawn directly onto
 * the supplied CanvasRenderingContext2D.
 *
 * @module PageCurve
 */

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

/** Default number of vertical strips the page is divided into. */
const DEFAULT_STRIPS = 24;

/** Maximum vertical displacement as a fraction of page height (at intensity 1). */
const MAX_DISPLACEMENT_RATIO = 0.045;

/**
 * How aggressively the strip width narrows near the spine.
 * Higher = more foreshortening.  0 = uniform strip widths.
 */
const FORESHORTEN_STRENGTH = 0.30;

/** Lighting: darkest value near the spine (0 = black, 1 = transparent). */
const SHADOW_OPACITY_MIN = 0.0;

/** Lighting: darkest overlay alpha at the spine valley. */
const SHADOW_OPACITY_MAX = 0.22;

/** Lighting: brightest highlight alpha at the crest (glossy reflection). */
const HIGHLIGHT_OPACITY_MAX = 0.25;

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/**
 * Build the displacement curve for each strip.
 *
 * The curve is a modified sine that peaks near the spine side (strip 0 for a
 * right page, strip N-1 for a left page) and flattens toward the outer edge.
 *
 * @param {number} numStrips
 * @param {number} intensity     0-1 normalised
 * @param {boolean} isLeftPage
 * @param {number} pageHeight
 * @returns {{ dy: number[], widthScale: number[], lightFactor: number[] }}
 */
function buildCurve(numStrips, intensity, isLeftPage, pageHeight) {
  const dy = new Array(numStrips);
  const widthScale = new Array(numStrips);
  const lightFactor = new Array(numStrips); // -1 (shadow) .. +1 (highlight)

  const maxDy = pageHeight * MAX_DISPLACEMENT_RATIO * intensity;

  for (let i = 0; i < numStrips; i++) {
    // t runs 0 → 1 from spine edge to outer edge
    let t = i / (numStrips - 1);
    if (isLeftPage) t = 1 - t;

    // ----- Vertical displacement (page lift) -----
    // Combination: steep rise near spine, gentle falloff toward outer edge
    // Using a power-sine blend for a natural paper curve
    const curveT = 1 - t; // 0 at outer edge, 1 at spine
    const sinePart = Math.sin(curveT * Math.PI * 0.5);          // fast initial lift
    const cosinePart = 1 - Math.cos(curveT * Math.PI * 0.35);   // broader lift
    const blend = sinePart * 0.6 + cosinePart * 0.4;
    dy[i] = -blend * maxDy; // negative = upward on canvas

    // ----- Foreshortening (strip width compression near spine) -----
    // Strips near the spine are narrower (viewed more edge-on)
    const compress = 1 - FORESHORTEN_STRENGTH * intensity * curveT * curveT;
    widthScale[i] = Math.max(compress, 0.55);

    // ----- Lighting factor -----
    // Combine diffuse slope lighting with a sharp glossy specular peak.
    let diffuse = 0;
    if (i > 0) {
      const prevDy = dy[i - 1] ?? dy[i];
      const slope = (dy[i] - prevDy); // negative slope = rising toward viewer
      diffuse = Math.max(-1, Math.min(1, slope / (maxDy * 0.15 + 0.001)));
    }
    
    // Sharp Gaussian peak for the specular highlight near the top of the curve
    const specular = Math.exp(-Math.pow((curveT - 0.65) / 0.12, 2));
    
    // Blend: negative diffuse creates spine shadow, specular overrides for gloss
    lightFactor[i] = diffuse < 0 ? diffuse : Math.max(diffuse, specular);
  }

  // Smooth lighting factors with a simple box-blur pass
  const smoothed = new Array(numStrips);
  const radius = 2;
  for (let i = 0; i < numStrips; i++) {
    let sum = 0;
    let count = 0;
    for (let j = Math.max(0, i - radius); j <= Math.min(numStrips - 1, i + radius); j++) {
      sum += lightFactor[j];
      count++;
    }
    smoothed[i] = sum / count;
  }

  return { dy, widthScale, lightFactor: smoothed };
}

/* ------------------------------------------------------------------ */
/*  Public API                                                         */
/* ------------------------------------------------------------------ */

/**
 * Render a single magazine page with a realistic open-book curve distortion.
 *
 * The image is sliced into vertical strips which are individually displaced,
 * scaled, and lit to simulate a page curving away from the spine.
 *
 * @param {CanvasRenderingContext2D} ctx  – destination context
 * @param {HTMLImageElement|HTMLCanvasElement|ImageBitmap} image – source page
 * @param {number} x        – left edge of the destination area
 * @param {number} y        – top edge of the destination area
 * @param {number} width    – full width of the destination area
 * @param {number} height   – full height of the destination area
 * @param {object} [options]
 * @param {number} [options.curveIntensity=0.15]  – 0 (flat) to 1 (extreme)
 * @param {boolean} [options.isLeftPage=false]     – true for the left page
 * @param {boolean} [options.lightingEnabled=true] – apply lighting gradients
 * @param {number} [options.numStrips]             – override strip count
 */
export function renderPageCurve(ctx, image, x, y, width, height, options = {}) {
  const {
    curveIntensity = 0.15,
    isLeftPage = false,
    lightingEnabled = true,
    numStrips = DEFAULT_STRIPS,
  } = options;

  if (!image || width <= 0 || height <= 0) return;

  const imgW = image.width || image.naturalWidth || width;
  const imgH = image.height || image.naturalHeight || height;

  // Build the displacement & lighting arrays
  const { dy, widthScale, lightFactor } = buildCurve(numStrips, curveIntensity, isLeftPage, height);

  // Compute actual strip destination widths (accounting for foreshortening)
  const rawWidths = widthScale.map((s) => s);
  const totalRaw = rawWidths.reduce((a, b) => a + b, 0);
  const stripDestWidths = rawWidths.map((w) => (w / totalRaw) * width);

  // Source strip width (uniform slices of the source image)
  const srcStripW = imgW / numStrips;

  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Running X position for destination strips
  let dx = x;

  for (let i = 0; i < numStrips; i++) {
    const destW = stripDestWidths[i];
    const destH = height;

    // Source rectangle
    const sx = i * srcStripW;
    const sy = 0;
    const sw = srcStripW + 0.5; // tiny overlap prevents sub-pixel gaps
    const sh = imgH;

    // Destination rectangle (displaced vertically)
    const destX = dx;
    const destY = y + dy[i];

    ctx.save();

    // Clip to strip bounds to prevent bleed (anti-aliasing overlap)
    ctx.beginPath();
    ctx.rect(destX - 0.5, y + Math.min(...dy) - 2, destW + 1, destH + Math.abs(Math.min(...dy)) + 4);
    ctx.clip();

    // Draw the strip
    ctx.drawImage(image, sx, sy, sw, sh, destX, destY, destW + 0.5, destH);

    // ----- Lighting overlay -----
    if (lightingEnabled) {
      const lf = lightFactor[i];

      if (lf < -0.05) {
        // Shadow (near spine valley)
        const alpha = Math.min(SHADOW_OPACITY_MAX, Math.abs(lf) * SHADOW_OPACITY_MAX) * curveIntensity * 4;
        ctx.fillStyle = `rgba(0, 0, 0, ${alpha.toFixed(4)})`;
        ctx.fillRect(destX - 0.5, destY, destW + 1, destH);
      } else if (lf > 0.05) {
        // Glossy Specular Highlight (at crest)
        // We use a non-linear power curve to make the core of the highlight very bright
        const glossFactor = Math.pow(lf, 2); 
        const alpha = Math.min(HIGHLIGHT_OPACITY_MAX, glossFactor * HIGHLIGHT_OPACITY_MAX) * (0.5 + curveIntensity * 3);
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha.toFixed(4)})`;
        ctx.fillRect(destX - 0.5, destY, destW + 1, destH);
      }

      // Additional spine-edge darkening gradient for the 2-3 strips closest to the spine
      const spineIndex = isLeftPage ? numStrips - 1 : 0;
      const distFromSpine = Math.abs(i - spineIndex);
      if (distFromSpine <= 2) {
        const spineAlpha = (1 - distFromSpine / 3) * 0.12 * curveIntensity * 4;
        ctx.fillStyle = `rgba(0, 0, 0, ${spineAlpha.toFixed(4)})`;
        ctx.fillRect(destX - 0.5, destY, destW + 1, destH);
      }
    }

    ctx.restore();

    dx += destW;
  }

  ctx.restore();
}

/**
 * Convenience: render a page curve onto a new offscreen canvas and return it.
 *
 * @param {HTMLImageElement|HTMLCanvasElement|ImageBitmap} image
 * @param {number} width
 * @param {number} height
 * @param {object} [options] – same as renderPageCurve options
 * @returns {HTMLCanvasElement}
 */
export function renderPageCurveToCanvas(image, width, height, options = {}) {
  const canvas = document.createElement('canvas');
  // Extra vertical headroom for the upward displacement
  const headroom = Math.ceil(height * MAX_DISPLACEMENT_RATIO * (options.curveIntensity ?? 0.15));
  canvas.width = width;
  canvas.height = height + headroom;
  const ctx = canvas.getContext('2d');
  renderPageCurve(ctx, image, 0, headroom, width, height, options);
  return canvas;
}
